import { supabase } from "@/lib/supabase/client";

export type SiteContent = {
  id: string;
  section: string;
  content: any;
  updated_at: string;
};

type SiteContentResult = SiteContent | SiteContent[] | null;

const siteContentCache = new Map<string, SiteContentResult>();
const siteContentRequests = new Map<string, Promise<SiteContentResult>>();

function getSiteContentCacheKey(section?: string) {
  return section ?? "__all__";
}

function clearSiteContentCache(section?: string) {
  if (section) {
    const cacheKey = getSiteContentCacheKey(section);
    siteContentCache.delete(cacheKey);
    siteContentRequests.delete(cacheKey);
    siteContentCache.delete(getSiteContentCacheKey());
    siteContentRequests.delete(getSiteContentCacheKey());
    return;
  }

  siteContentCache.clear();
  siteContentRequests.clear();
}

export async function fetchSiteContent(section?: string) {
  const cacheKey = getSiteContentCacheKey(section);

  if (siteContentCache.has(cacheKey)) {
    return siteContentCache.get(cacheKey) ?? null;
  }

  const pendingRequest = siteContentRequests.get(cacheKey);
  if (pendingRequest) {
    return pendingRequest;
  }

  const request = (async () => {
  let query = supabase.from('site_content').select('*');

  if (section) {
    query = query.eq('section', section);
  }

  const { data, error } = await query;

  if (error) {
    console.error("Error fetching site content:", error);
    return null;
  }

  const result = section && data ? data[0] || null : data;
  siteContentCache.set(cacheKey, result);

  return result;
  })();

  siteContentRequests.set(cacheKey, request);

  try {
    return await request;
  } finally {
    siteContentRequests.delete(cacheKey);
  }

}

export async function updateSiteContent(section: string, content: any) {
  const { error } = await supabase
    .from('site_content')
    .upsert({
      section,
      content,
      updated_at: new Date().toISOString()
    }, {
      onConflict: 'section'
    });

  if (error) {
    console.error("Error updating site content:", error);
    throw error;
  }

  clearSiteContentCache(section);
}
